import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { onUnmounted, ref } from "vue";
import { useSiteWideHubManager } from "~/composables/useSiteWideHubManager";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { hubManager } from "~/utils/hubManager";

export interface HousingApplicationEditorUser {
  connectionId: string;
  userName: string;
  joinedAt: string;
  isIdle: boolean;
}

export function useLiveHousingApplicationManager() {
  const myInfoStore = useMyInfoStore();
  const { isServerOffline } = useSiteWideHubManager();

  const isConnected = ref(false);
  const viewerMap = ref<Record<string, string[]>>({});
  const applicationEditors = ref<HousingApplicationEditorUser[]>([]);

  let connection: HubConnection | null = null;
  let appGuid: string | null = null;

  let viewerListHandler: ((data: Record<string, string[]>) => void) | null =
    null;
  let editorsHandler:
    | ((data: { users: HousingApplicationEditorUser[] }) => void)
    | null = null;
  let controlRequestHandler: ((request: any) => void) | null = null;
  let controlResponseHandler: ((response: any) => void) | null = null;

  const startConnection = async (applicationGuid: string): Promise<void> => {
    if (!applicationGuid) {
      console.warn("Application GUID is not provided.");
      return;
    }

    if (isServerOffline.value) return;

    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 1 && connection) {
          try {
            if (viewerListHandler) {
              connection.off("ApplicationViewerListUpdated", viewerListHandler);
              viewerListHandler = null;
            }
            if (editorsHandler) {
              connection.off("ApplicationEditors", editorsHandler);
              editorsHandler = null;
            }
            if (controlRequestHandler) {
              connection.off("ControlRequestReceived", controlRequestHandler);
              controlRequestHandler = null;
            }
            if (controlResponseHandler) {
              connection.off("ControlRequestResponse", controlResponseHandler);
              controlResponseHandler = null;
            }
          } catch (cleanupError) {
            console.warn(
              "Error cleaning up handlers during retry:",
              cleanupError,
            );
          }
          connection = null;
        }

        connection = await hubManager.getHub(
          "housingApplication",
          "/housingApplication",
        );

        if (!connection) {
          throw new Error(
            "Failed to create SignalR connection - hubManager returned null",
          );
        }

        if (!viewerListHandler) {
          viewerListHandler = (data: Record<string, string[]>) => {
            viewerMap.value = data;
          };
          connection.on("ApplicationViewerListUpdated", viewerListHandler);
        }

        if (!editorsHandler) {
          editorsHandler = (data: {
            users: HousingApplicationEditorUser[];
          }) => {
            applicationEditors.value = data.users;
          };
          connection.on("ApplicationEditors", editorsHandler);
        }

        if (connection.state !== HubConnectionState.Connected) {
          throw new Error(
            `SignalR connection is not in Connected state. Current state: ${connection.state}`,
          );
        }

        const joinTimeout = 10000;
        const joinPromise = connection.invoke(
          "JoinApplicationGroup",
          applicationGuid,
          myInfoStore.userInfo.loginName,
        );

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("JoinApplicationGroup timed out")),
            joinTimeout,
          );
        });

        await Promise.race([joinPromise, timeoutPromise]);

        isConnected.value = true;
        appGuid = applicationGuid;
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (isServerOffline.value) {
          console.debug(
            `SignalR start error (attempt ${attempt}/${maxRetries}) - server is offline:`,
            lastError,
          );
        } else {
          console.error(
            `SignalR start error (attempt ${attempt}/${maxRetries}):`,
            lastError,
          );
        }

        isConnected.value = false;
        appGuid = null;

        if (viewerListHandler && connection) {
          try {
            connection.off("ApplicationViewerListUpdated", viewerListHandler);
          } catch {
            /* ignore */
          }
          viewerListHandler = null;
        }
        if (editorsHandler && connection) {
          try {
            connection.off("ApplicationEditors", editorsHandler);
          } catch {
            /* ignore */
          }
          editorsHandler = null;
        }

        if (attempt < maxRetries && !isServerOffline.value) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else if (isServerOffline.value) {
          break;
        }
      }
    }

    if (!isServerOffline.value) {
      throw (
        lastError || new Error("SignalR connection failed after all retries")
      );
    }
  };

  const onControlRequestReceived = (callback: (request: any) => void): void => {
    if (controlRequestHandler && connection) {
      connection.off("ControlRequestReceived", controlRequestHandler);
    }
    controlRequestHandler = callback;
    connection?.on("ControlRequestReceived", controlRequestHandler);
  };

  const onControlRequestResponse = (
    callback: (response: any) => void,
  ): void => {
    if (controlResponseHandler && connection) {
      connection.off("ControlRequestResponse", controlResponseHandler);
    }
    controlResponseHandler = callback;
    connection?.on("ControlRequestResponse", controlResponseHandler);
  };

  const requestApplicationControl = async (
    applicationGuid: string,
  ): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke("RequestApplicationControl", applicationGuid);
    }
  };

  const respondToControlRequest = async (
    applicationGuid: string,
    requestingConnectionId: string,
    approved: boolean,
  ): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke(
        "RespondToControlRequest",
        applicationGuid,
        requestingConnectionId,
        approved,
      );
    }
  };

  const reportIdle = async (applicationGuid: string): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke("ReportIdle", applicationGuid);
    }
  };

  const reportActive = async (applicationGuid: string): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke("ReportActive", applicationGuid);
    }
  };

  const leaveApplicationGroup = async (
    applicationGuid: string,
  ): Promise<void> => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await connection.invoke(
        "LeaveApplicationGroup",
        applicationGuid,
        myInfoStore.userInfo.loginName,
      );
    }
  };

  const stopConnection = async (): Promise<void> => {
    try {
      if (
        connection &&
        connection.state === HubConnectionState.Connected &&
        appGuid
      ) {
        await leaveApplicationGroup(appGuid);
      }

      if (viewerListHandler && connection) {
        try {
          connection.off("ApplicationViewerListUpdated", viewerListHandler);
        } catch (error) {
          console.warn(
            "Error removing ApplicationViewerListUpdated handler:",
            error,
          );
        }
        viewerListHandler = null;
      }

      if (editorsHandler && connection) {
        try {
          connection.off("ApplicationEditors", editorsHandler);
        } catch {
          /* ignore */
        }
        editorsHandler = null;
      }

      if (controlRequestHandler && connection) {
        try {
          connection.off("ControlRequestReceived", controlRequestHandler);
        } catch {
          /* ignore */
        }
        controlRequestHandler = null;
      }

      if (controlResponseHandler && connection) {
        try {
          connection.off("ControlRequestResponse", controlResponseHandler);
        } catch {
          /* ignore */
        }
        controlResponseHandler = null;
      }

      isConnected.value = false;
      applicationEditors.value = [];

      if (appGuid) {
        const newMap = { ...viewerMap.value };
        delete newMap[appGuid];
        viewerMap.value = newMap;
      }
    } catch (error) {
      console.error("Error stopping SignalR connection:", error);
      isConnected.value = false;
      viewerListHandler = null;
      editorsHandler = null;
      controlRequestHandler = null;
      controlResponseHandler = null;
    } finally {
      appGuid = null;
    }
  };

  onUnmounted(async () => {
    await stopConnection();
  });

  return {
    isConnected,
    viewerMap,
    applicationEditors,
    startConnection,
    stopConnection,
    leaveApplicationGroup,
    onControlRequestReceived,
    onControlRequestResponse,
    requestApplicationControl,
    respondToControlRequest,
    reportIdle,
    reportActive,
  };
}
